import {
  type BundledQuoteGrouping,
  CartGroup,
  type CartGrouping,
  type GroupedCart,
  type GroupedCartEntry,
  type LineItem,
} from "../../models/CartData";
import { groupByFn } from "~/common/utils/reducers";
import { selectDeepProductDescriptor } from "~/domains/product/redux/selectors";
import { type RootState } from "~/app/store";

export const selectCart = makeCartGroupingSelector(
  () => true,
  () => false,
);
type EntryClassifier = (packEntry: LineItem, store: RootState) => boolean;

export function makeCartGroupingSelector(isOrderable: EntryClassifier, isQuotable: EntryClassifier) {
  return (state: RootState): GroupedCart => {
    const cart = state.cart;
    const productsStore = state.products;

    const orderables: CartGrouping = {
      type: CartGroup.ORDER,
      entries: [],
    };
    const quotables: CartGrouping = {
      type: CartGroup.QUOTE,
      entries: [],
    };

    const bundleMap: Dict<BundledQuoteGrouping> = {};
    const bundles: BundledQuoteGrouping[] = [];

    const getBundledEntry = (quoteNumber: string, cartEntryId: number) => {
      let bundle = bundleMap[quoteNumber];
      if (!bundle) {
        bundle = {
          type: CartGroup.BUNDLE,
          quoteNumber: quoteNumber,
          entries: [],
        };
        bundles.push(bundle);
        bundleMap[quoteNumber] = bundle;
      }

      let entry: GroupedCartEntry | undefined = bundle.entries.filter((entry) => entry.cartEntryId === cartEntryId)[0];
      if (!entry) {
        entry = {
          cartEntryId,
          packGroups: [],
        };
        bundle.entries.push(entry);
      }
      return entry;
    };

    cart.cartEntries
      .filter((entryId) => {
        const cartEntry = cart.cartEntriesById[entryId];
        return cartEntry.entryNumber !== -1;
      })
      .forEach((entryId) => {
        const cartEntry = cart.cartEntriesById[entryId];

        const ordreableEntry: GroupedCartEntry = {
          cartEntryId: entryId,
          packGroups: [],
        };

        const quoteableEntry: GroupedCartEntry = {
          cartEntryId: entryId,
          packGroups: [],
        };

        const keyParts = ["contractLine", "contractNumber", "quoteLine", "quoteNumber"] as const;

        Object.values(
          groupByFn(
            cartEntry.packEntryIds.map(($) => cart.packEntriesById[$]),
            (obj) => keyParts.map(($) => obj[$]).join("|"),
            (item) => item,
          ),
        ).forEach((lineItems) => {
          const entriesGroup: number[] = [];
          let entriesQuoteNumber: string = "";
          const orderableGroup: number[] = [];
          const quoteableGroup: number[] = [];

          lineItems
            .filter((lineItem) => lineItem.entryNumber !== -1)
            .forEach((lineItem) => {
              const packEntry = lineItem;
              const packEntryId = lineItem.id;
              const productDescriptor = selectDeepProductDescriptor(productsStore, {
                productCode: packEntry.productCode,
                priceReference: packEntry.priceReference,
              });

              if (packEntry.quoteNumber && packEntry.isBundled) {
                entriesGroup.push(packEntryId);
                entriesQuoteNumber = packEntry.quoteNumber;
              } else if (isOrderable(packEntry, state)) {
                orderableGroup.push(packEntryId);
              } else if (isQuotable(packEntry, state)) {
                quoteableGroup.push(packEntryId);
              } else {
                console.error(`Line item with id ${packEntryId} has not been classified to any grouping`);
              }
            });

          if (entriesGroup.length) {
            getBundledEntry(entriesQuoteNumber, cartEntry.id).packGroups.push({ packEntryIds: entriesGroup });
          }
          if (orderableGroup.length) {
            ordreableEntry.packGroups.push({ packEntryIds: orderableGroup });
          }
          if (quoteableGroup.length) {
            quoteableEntry.packGroups.push({ packEntryIds: quoteableGroup });
          }
        });

        if (ordreableEntry.packGroups.length > 0) {
          orderables.entries.push(ordreableEntry);
        }
        if (quoteableEntry.packGroups.length > 0) {
          quotables.entries.push(quoteableEntry);
        }
      });

    return { orderables, quotables, bundles };
  };
}
